import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleAuthProvider, signInWithCredential } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { formatRelative, formatAbsolute } from '../utils/time';
import DeleteAccountModal from './DeleteAccountModal';
import DataExportImportModal from './DataExportImportModal';

/**
 * AccountPopover — anchored panel that drops down from the user chip in
 * the desktop header. Two flavours:
 *
 *   - Real user: avatar, name, email, provider, lightweight stats,
 *     member-since, sign-out.
 *   - Anonymous (guest): explanation that data lives on this device only,
 *     plus inline conversion CTAs (Google + magic link). Sign-out becomes
 *     "end guest session" since there's no saved account to come back to.
 *
 * The same component is used in mobile Settings (via `inline` prop) — it
 * drops the floating shell and renders content inline as a settings row.
 */
export default function AccountPopover({
  user,
  boards = [],
  listsCount = 0,
  onClose,
  onSignOut,
  onLinkGoogle,
  onLinkMagicLink,
  inline = false,
}) {
  const ref = useRef(null);

  // Outside-click + Escape dismiss — only when floating.
  // Walter 2026-05-10: the popover wasn't closing on outside-click for
  // Walter — root cause was the popover-opening click bubbling up to
  // the document mousedown listener INSIDE the same React render
  // tick, before ref.current was painted. The listener saw the click
  // target as outside (because ref.current was still null) and...
  // didn't close (the `&& ref.current` guard ate it) — but it also
  // never closed after, because something about the listener install
  // timing on Walter's machine was racy. Belt-and-braces fix: defer
  // listener install by one tick (rAF + setTimeout(0)) so the
  // opening click is fully done before we start listening, AND
  // listen on `click` (mouseup-equivalent) in addition to mousedown.
  useEffect(() => {
    if (inline) return;
    const onDown = (e) => {
      const el = ref.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    let installed = false;
    const installTimer = setTimeout(() => {
      document.addEventListener('mousedown', onDown);
      document.addEventListener('click', onDown);
      document.addEventListener('keydown', onKey);
      installed = true;
    }, 0);
    return () => {
      clearTimeout(installTimer);
      if (installed) {
        document.removeEventListener('mousedown', onDown);
        document.removeEventListener('click', onDown);
        document.removeEventListener('keydown', onKey);
      }
    };
  }, [inline, onClose]);

  const isGuest = !!user?.isAnonymous;

  const body = isGuest ? (
    <GuestBody onLinkGoogle={onLinkGoogle} onLinkMagicLink={onLinkMagicLink} />
  ) : (
    <RealUserBody
      user={user}
      boards={boards}
      listsCount={listsCount}
      // Mobile/inline mode hides the sign-out row — the parent settings
      // modal already has its own sign-out button at the bottom.
      onSignOut={inline ? null : onSignOut}
      // Walter 2026-05-06 round 7: trash entry is now ALWAYS shown
      // here. On mobile it's the only path; on desktop it's a backup
      // for users who collapsed the sidebar (the toggle is subtle
      // enough that the sidebar footer can become unreachable).
      showTrashRow={true}
    />
  );

  if (inline) {
    // Settings-row mode — caller positions us; we render content only.
    return body;
  }

  return (
    <div
      ref={ref}
      // Walter 2026-05-26 — stop ALL clicks inside the popover from
      // bubbling to the document. Background: the click-outside
      // detector listens on both mousedown AND click (line 55-56). For
      // most internal buttons that's harmless — the button's onClick
      // fires first, then `el.contains(e.target)` returns true and the
      // listener no-ops. BUT for any internal button whose onClick
      // unmounts itself by toggling state ("or get a sign-in link by
      // email" → setShowEmail(true) replaces the button with the
      // form), React re-renders BEFORE the document `click` listener
      // runs. By the time the listener checks `el.contains(e.target)`,
      // the original button DOM node has been removed from the tree —
      // contains() returns false — the listener thinks the click was
      // outside the popover and closes it. The mousedown listener was
      // unaffected because mousedown fires before React's re-render,
      // but click fires after.
      // Symptom Walter hit: clicked "or get a sign-in link by email"
      // → popover disappeared and the form never showed.
      // Fix: trap mousedown + click at the wrapper. Internal clicks
      // never reach the document listener; the listener only sees
      // genuine outside clicks and the close-on-outside-click still
      // works correctly.
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', top: '100%', right: 0, marginTop: '6px',
        background: 'var(--surface)',
        border: '1px solid var(--border-bright)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow-popover)',
        zIndex: 1000, minWidth: 280, maxWidth: 320,
        animation: 'lists-fadein 0.12s ease',
      }}
    >
      {body}
    </div>
  );
}

// ───────── real user body ─────────

function RealUserBody({ user, boards, listsCount, onSignOut, showTrashRow }) {
  const provider = providerLabel(user);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const created = user?.metadata?.creationTime
    ? new Date(user.metadata.creationTime)
    : null;

  return (
    <div>
      {/* Identity block */}
      <div style={{ padding: '14px 16px 12px', display: 'flex', gap: '10px' }}>
        <ProviderBadge provider={provider} photoURL={user?.photoURL} />
        <div style={{ minWidth: 0, flex: 1 }}>
          {user?.displayName && (
            <div style={{
              fontFamily: 'var(--mono)',
              fontSize: 'calc(12px * var(--ui-scale, 1))',
              fontWeight: 600,
              color: 'var(--text)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {user.displayName}
            </div>
          )}
          <div style={{
            fontFamily: 'var(--mono)',
            fontSize: 'calc(11px * var(--ui-scale, 1))',
            color: user?.displayName ? 'var(--text-dim)' : 'var(--text)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {user?.email || '(no email)'}
          </div>
          <div style={{
            marginTop: '3px',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(10px * var(--ui-scale, 1))',
            color: 'var(--text-dimmer)',
          }}>
            signed in via {provider.label}
          </div>
        </div>
      </div>

      <Divider />

      {/* Lightweight stats. Member-since uses formatRelative for short
          ages ("2d", "3w") and falls back to absolute date once a few
          months have passed. The hover-tooltip shows the full date. */}
      <div style={{
        padding: '10px 16px',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(10px * var(--ui-scale, 1))',
        color: 'var(--text-dim)',
        lineHeight: 1.7,
      }}>
        <div>
          {boards.length} {boards.length === 1 ? 'board' : 'boards'}
          {' · '}
          {listsCount} {listsCount === 1 ? 'list' : 'lists'}
        </div>
        {created && (
          <div title={formatAbsolute(created)}>
            member since {formatMemberSince(created)}
          </div>
        )}
      </div>

      {showTrashRow && (
        <>
          <Divider />
          <TrashMenuRow />
        </>
      )}

      <Divider />
      <MenuRow onClick={() => setShowDataModal(true)}>
        <DataIcon />
        import / export
      </MenuRow>

      <Divider />
      <MenuRow onClick={() => setShowDeleteModal(true)}>
        <DangerIcon />
        delete my account
      </MenuRow>

      {onSignOut && (
        <>
          <Divider />
          <MenuRow danger onClick={onSignOut}>
            <SignOutIcon />
            sign out
          </MenuRow>
        </>
      )}

      <DeleteAccountModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        user={user}
      />

      <DataExportImportModal
        open={showDataModal}
        onClose={() => setShowDataModal(false)}
        user={user}
      />
    </div>
  );
}

function DangerIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M7 1L1 12.5h12L7 1zM7 5v3M7 10.5v.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Inline component so the navigate hook is local to where it's used.
// Sits between the stats row and sign-out.
function TrashMenuRow() {
  const navigate = useNavigate();
  return (
    <MenuRow onClick={() => navigate('/trash')}>
      <TrashIcon />
      trash
    </MenuRow>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M3 4h8M5 4V2.5h4V4M4 4v8h6V4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DataIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M3 4l2-2 2 2M5 2v7M11 10l-2 2-2-2M9 12V5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ───────── guest body ─────────

function GuestBody({ onLinkGoogle, onLinkMagicLink }) {
  const [magicEmail, setMagicEmail] = useState('');
  const [showEmail, setShowEmail] = useState(false);
  const [magicState, setMagicState] = useState('idle'); // 'idle' | 'sending' | 'sent'
  const [error, setError] = useState('');
  // Holds the Google credential from a credential-already-in-use error so we
  // can offer an IN-APP confirm (see handleGoogle). 'nocred' = error carried
  // no usable credential; null = no prompt showing.
  const [existingCred, setExistingCred] = useState(null);

  const handleGoogle = async () => {
    setError('');
    setExistingCred(null);
    try {
      await onLinkGoogle();
    } catch (e) {
      if (e?.code === 'auth/credential-already-in-use') {
        // The Google account already has its own listicity user, so the
        // anonymous→Google LINK can't happen. Firebase hands the Google
        // credential back on the error, and we CAN sign straight into that
        // account with it — but we must confirm first (guest data on this
        // device is left behind). Walter 2026-06-12: we surface that confirm
        // IN-APP, not via window.confirm — Chrome suppresses a native
        // confirm() here because it fires right as the Google popup steals
        // focus ("page is not the active tab"), so it silently returned
        // cancel and the sign-in bounced. Stash the credential; the inline
        // prompt below drives the actual signInWithCredential.
        setExistingCred(GoogleAuthProvider.credentialFromError(e) || 'nocred');
      } else if (e?.code === 'auth/popup-closed-by-user' || e?.code === 'auth/cancelled-popup-request') {
        // User dismissed the Google popup — not an error, stay quiet.
      } else {
        setError('Could not sign in. Please try again.');
      }
    }
  };

  // Confirmed (in-app) intent to sign into the pre-existing account.
  const handleSignIntoExisting = async () => {
    const cred = existingCred;
    setExistingCred(null);
    if (!cred || cred === 'nocred') {
      setError('Could not switch automatically — use Sign out, then Sign in with Google.');
      return;
    }
    try {
      // onAuthStateChanged takes over once this resolves and routes the
      // now-signed-in user to their board.
      await signInWithCredential(auth, cred);
    } catch (err) {
      setError('Could not switch to that account — try Sign out, then Sign in.');
    }
  };

  const handleMagic = async (e) => {
    e.preventDefault();
    if (!/\S+@\S+\.\S+/.test(magicEmail.trim())) {
      setError('Enter a valid email');
      return;
    }
    setError('');
    setMagicState('sending');
    try {
      await onLinkMagicLink(magicEmail.trim());
      setMagicState('sent');
    } catch (err) {
      setError('Could not send the link');
      setMagicState('idle');
    }
  };

  return (
    <div>
      <div style={{ padding: '14px 16px 4px' }}>
        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: 'calc(12px * var(--ui-scale, 1))',
          fontWeight: 600,
          color: 'var(--text)',
          marginBottom: '6px',
        }}>
          guest session
        </div>
        <div style={{
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          lineHeight: 1.55,
        }}>
          You're trying listicity as a guest. Your boards are saved on this
          device only — sign in to keep them safe and sync across devices.
        </div>
      </div>

      <div style={{ padding: '12px 16px 14px' }}>
        {magicState === 'sent' ? (
          <div style={{
            padding: '10px 12px',
            background: 'var(--accent-wash)',
            border: '1px solid var(--accent-dim)',
            borderRadius: 'var(--radius)',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(10px * var(--ui-scale, 1))',
            color: 'var(--text)',
            lineHeight: 1.5,
          }}>
            ✓ Link sent to <span style={{ color: 'var(--accent)' }}>{magicEmail}</span>.
            Click it from this device to keep your guest data. The link
            takes a minute or two; if you don't see it, check spam.
          </div>
        ) : existingCred ? (
          <div style={{
            padding: '12px',
            background: 'var(--accent-wash)',
            border: '1px solid var(--accent-dim)',
            borderRadius: 'var(--radius)',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(10px * var(--ui-scale, 1))',
            color: 'var(--text)',
            lineHeight: 1.5,
          }}>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>
              that account already exists
            </div>
            <div style={{ color: 'var(--text-dim)', marginBottom: '10px' }}>
              That Google account is already a listicity user. Sign into it?
              Any guest data on this device will be left behind.
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleSignIntoExisting}
                style={{
                  flex: 1, padding: '7px 10px',
                  background: 'var(--accent)', color: 'var(--bg)',
                  border: '1px solid var(--accent)', borderRadius: 'var(--radius)',
                  fontFamily: 'var(--mono)', fontSize: 'inherit', fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                sign into it
              </button>
              <button
                onClick={() => setExistingCred(null)}
                style={{
                  padding: '7px 10px',
                  background: 'transparent', color: 'var(--text-dim)',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                  fontFamily: 'var(--mono)', fontSize: 'inherit', cursor: 'pointer',
                }}
              >
                cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Walter 2026-05-25 (Bug #3 — round-2 tester report): the
                error display used to live INSIDE the magic-link form
                below, but `setError` also fires from the Google path
                (handleGoogle's catch). If the user clicked Google and
                it failed, error was set but invisible (form not
                expanded) — "Sign in with Google fails silently."
                Hoisting the error display above both buttons makes
                failures from either path visible. */}
            {error && (
              <div style={{
                padding: '8px 10px',
                marginBottom: '10px',
                background: 'rgba(192, 50, 50, 0.08)',
                border: '1px solid var(--danger)',
                borderRadius: 'var(--radius)',
                color: 'var(--danger)',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(10px * var(--ui-scale, 1))',
                lineHeight: 1.5,
              }}>
                {error}
              </div>
            )}
            <ConvertButton
              accent
              onClick={handleGoogle}
              icon={<GoogleIcon />}
            >
              Sign in with Google
            </ConvertButton>

            {showEmail ? (
              <form onSubmit={handleMagic} style={{ marginTop: '8px' }}>
                <input
                  type="email"
                  value={magicEmail}
                  onChange={(e) => { setMagicEmail(e.target.value); setError(''); }}
                  placeholder="your@email.com"
                  autoFocus
                  disabled={magicState === 'sending'}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '8px 12px',
                    background: 'var(--bg)',
                    border: '1px solid var(--border-bright)',
                    borderRadius: 'var(--radius)',
                    color: 'var(--text)',
                    fontFamily: 'var(--mono)',
                    fontSize: 'calc(11px * var(--ui-scale, 1))',
                    outline: 'none',
                    marginBottom: '6px',
                  }}
                />
                <ConvertButton type="submit" disabled={magicState === 'sending'}>
                  {magicState === 'sending' ? 'sending…' : 'Email me a link'}
                </ConvertButton>
                {/* Inner error div removed — display hoisted above
                    both buttons (see comment up there). Single source
                    of truth for sign-in failures from either path. */}
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setShowEmail(true)}
                style={{
                  marginTop: '10px',
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-dim)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(10px * var(--ui-scale, 1))',
                  cursor: 'pointer',
                  padding: '4px',
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dim)'}
              >
                or get a sign-in link by email
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ───────── small parts ─────────

function ProviderBadge({ provider, photoURL }) {
  if (photoURL) {
    return (
      <img
        src={photoURL}
        alt=""
        style={{
          width: 36, height: 36, borderRadius: '50%',
          flexShrink: 0,
          objectFit: 'cover',
          border: '1px solid var(--border)',
        }}
      />
    );
  }
  return (
    <div style={{
      width: 36, height: 36, borderRadius: '50%',
      flexShrink: 0,
      background: 'var(--surface2)',
      border: '1px solid var(--border-bright)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {provider.id === 'google' ? <GoogleIcon size={18} /> : <EmailIcon />}
    </div>
  );
}

function ConvertButton({ children, onClick, type = 'button', accent, icon, disabled }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%', boxSizing: 'border-box',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: '8px',
        background: accent ? 'var(--accent)' : 'transparent',
        color: accent ? 'var(--bg)' : 'var(--accent)',
        border: accent ? '1px solid var(--accent)' : '1px solid var(--accent-dim)',
        borderRadius: 'var(--radius)',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(11px * var(--ui-scale, 1))',
        fontWeight: accent ? 600 : 400,
        padding: '8px 12px',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'background 0.15s, color 0.15s',
      }}
    >
      {icon}
      {children}
    </button>
  );
}

function MenuRow({ children, onClick, danger, disabled }) {
  const [hover, setHover] = useState(false);
  const base = danger ? 'var(--danger)' : 'var(--text-dim)';
  const hi = danger ? 'var(--danger)' : 'var(--text)';
  return (
    <div
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => !disabled && setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '10px 16px',
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(11px * var(--ui-scale, 1))',
        color: hover && !disabled ? hi : base,
        background: hover && !disabled ? 'var(--surface2)' : 'transparent',
        display: 'flex', alignItems: 'center', gap: '8px',
        transition: 'background 0.1s, color 0.1s',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      {children}
    </div>
  );
}

function Divider() {
  return <div style={{ height: '1px', background: 'var(--border)' }} />;
}

// ───────── icons ─────────

function GoogleIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1.5" y="3" width="13" height="10" rx="1.5" stroke="var(--text-dim)" strokeWidth="1.3"/>
      <path d="M2 4l6 5 6-5" stroke="var(--text-dim)" strokeWidth="1.3" strokeLinejoin="round"/>
    </svg>
  );
}

function SignOutIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M6 1H2a1 1 0 00-1 1v10a1 1 0 001 1h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M9 4l3 3-3 3M12 7H5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ───────── helpers ─────────

function providerLabel(user) {
  const ids = user?.providerData?.map(p => p.providerId) || [];
  if (ids.includes('google.com')) return { id: 'google', label: 'Google' };
  if (ids.includes('password') || ids.includes('emailLink') || user?.email) {
    return { id: 'email', label: 'Email' };
  }
  return { id: 'unknown', label: 'this device' };
}

// Always an absolute date — "Apr 15, 2026". Cleaner and more truthful than
// a relative label here; the user wants to know when they joined, not how
// many weeks ago that was.
function formatMemberSince(date) {
  try {
    return date.toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return date.toDateString();
  }
}
